/**
 * 돌봄서비스 정보 컴포넌트
 * 각 서비스(장애인활동지원, 노인장기요양 등)의 상세 정보를 표시합니다.
 */

import styled from 'styled-components';
import { Container, SectionTitle, ListBox, BulletPoint } from './careStyles';
import { backgroundImages, sectionConfig } from '../../constant/careData';
import care24 from '../../assets/care24.png';

/**
 * CareServiceInfo 컴포넌트
 * @param {Object} props
 * @param {Array} props.List - 서비스 상세 정보 배열
 * @param {string} props.no - 서비스 번호 ("1"~"8")
 */
function CareServiceInfo({ List, no }) {
  /**
   * 리스트 아이템 렌더링 헬퍼 함수
   * 중첩 구조 지원: { text: string, sub: string[] }
   */
  const renderListItems = (value) => {
    const values = Array.isArray(value) ? value : [value];
    return values.map((item, index) => {
      // 중첩 구조인 경우
      if (typeof item === 'object' && item.text) {
        return (
          <div key={index}>
            <p>
              <BulletPoint>・</BulletPoint>
              <span>{item.text}</span>
            </p>
            {item.sub && (
              <SubList>
                {item.sub.map((subItem, subIndex) => (
                  <p key={subIndex}>
                    <BulletPoint>-</BulletPoint>
                    <span>{subItem}</span>
                  </p>
                ))}
              </SubList>
            )}
          </div>
        );
      }
      // 단순 문자열인 경우
      return (
        <p key={index}>
          <BulletPoint>・</BulletPoint>
          <span>{item}</span>
        </p>
      );
    });
  };

  /**
   * 섹션 콘텐츠 렌더링
   * sectionConfig를 확인하여 특수 렌더링 또는 일반 리스트 렌더링
   */
  const renderSectionContent = (item, index) => {
    const config = sectionConfig[no]?.[index];

    if (config?.type === 'payment-table') {
      return (
        <ListBox>
          <div>
            <PaymentTable />
            <p>{item.value}</p>
          </div>
        </ListBox>
      );
    }

    if (config?.type === 'website-link') {
      return (
        <WebsiteLinkWrapper>
          <WebsiteLink
            href={item.value}
            target="_blank"
            rel="noopener noreferrer"
          >
            사이트 바로가기 →
          </WebsiteLink>
        </WebsiteLinkWrapper>
      );
    }

    return <ListBox>{renderListItems(item.value)}</ListBox>;
  };

  const imageConfig = backgroundImages[no];
  const hasImage = Boolean(imageConfig);

  return (
    <Container>
      {/* 서비스별 이미지 (모바일용) */}
      {hasImage && (
        <ServiceImageMobile
          src={imageConfig.image}
          alt={`서비스 ${no} 아이콘`}
          $mobileWidth={imageConfig.mobileWidth}
          $mobileTop={imageConfig.mobileTop}
          $mobileRight={imageConfig.mobileRight}
        />
      )}

      {/* 서비스 개요 */}
      <SectionTitle>{List[0].name}</SectionTitle>
      <OverviewBox>
        <OverviewContent $noImage={!hasImage}>
          <p>{List[0].value}</p>
        </OverviewContent>
        {/* 서비스별 이미지 (데스크톱용) */}
        {hasImage && (
          <ServiceImage
            src={imageConfig.image}
            alt={`서비스 ${no} 아이콘`}
            $width={imageConfig.width}
            $top={imageConfig.top}
            $right={imageConfig.right}
          />
        )}
      </OverviewBox>

      {/* 나머지 섹션들 */}
      {List.slice(1).map((item, index) => {
        const sectionIndex = index + 1;
        if (!item || !item.name) return null;

        return (
          <div key={sectionIndex}>
            <SectionTitle>{item.name}</SectionTitle>
            {/* value가 있을 때만 내용 렌더링 */}
            {item.value && !(Array.isArray(item.value) && item.value.length === 0) && (
              renderSectionContent(item, sectionIndex)
            )}
          </div>
        );
      })}
    </Container>
  );
}

export default CareServiceInfo;

/* Styled Components - CareServiceInfo 전용 */

const OverviewBox = styled.div`
  position: relative;
  background-color: #f4f4f4;
  padding: 10px;
  margin-bottom: 40px;
  overflow: visible;
  box-sizing: border-box;
  max-width: 100%;

  @media (max-width: 767px) {
    padding: 15px;
    margin-bottom: 25px;
  }
`;

const OverviewContent = styled.div`
  padding: 10px 0;
  padding-right: ${(props) => (props.$noImage ? '10px' : '140px')};
  white-space: pre-wrap;
  box-sizing: border-box;
  width: 100%;

  p {
    font-family: 'koPubWorld DotumB';
    padding-left: 10px;
    font-size: 17px;
    line-height: 28px;
    margin: 0;
    word-break: keep-all;
    max-width: 100%;
  }

  @media (max-width: 767px) {
    padding: 5px 0;
    padding-right: 10px;

    p {
      font-size: 14px;
      line-height: 22px;
      padding-left: 5px;
    }
  }
`;

const ServiceImage = styled.img`
  position: absolute;
  top: ${(props) => props.$top || '0px'};
  right: ${(props) => props.$right || '10px'};
  width: ${(props) => props.$width || '200px'};
  max-width: 200px;
  height: auto;
  object-fit: contain;

  @media (max-width: 767px) {
    display: none;
  }
`;

const ServiceImageMobile = styled.img`
  display: none;

  @media (max-width: 767px) {
    display: block;
    position: absolute;
    width: ${(props) => props.$mobileWidth || '80px'};
    height: auto;
    object-fit: contain;
    margin-left: auto;
    margin-bottom: 15px;
    opacity: 0.8;
    top: ${(props) => props.$mobileTop || '-70px'};
    right: ${(props) => props.$mobileRight || '10px'};
    z-index: 100;
  }
`;

const PaymentTable = styled.div`
  width: 100%;
  height: 150px;
  background: url(${care24}) center top no-repeat;
  background-size: 100% 150px;

  @media (max-width: 767px) {
    height: 100px;
    background-size: 100% 100px;
  }
`;

const WebsiteLinkWrapper = styled.div`
  margin-bottom: 50px;
  box-sizing: border-box;
  max-width: 100%;
`;

const WebsiteLink = styled.a`
  display: inline-block;
  background-color: #e5857c;
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  text-decoration: none;
  font-family: 'Nanum Gothic Bold';
  font-size: 15px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #d16f68;
  }

  &:active {
    background-color: #c05f58;
  }

  @media (max-width: 767px) {
    font-size: 13px;
    padding: 10px 20px;
  }
`;

const SubList = styled.div`
  padding-left: 20px;

  @media (max-width: 767px) {
    padding-left: 16px;
  }
`;
